<template>
  <!-- ================================================================
       Dialog hiển thị danh sách phiếu thu chưa thanh toán
       Sử dụng: <unpaid-invoices-dialog ref="unpaidDialog" @payment="handlePayment" />
       Mở:      this.$refs.unpaidDialog.open(row, targetType)
       ================================================================ -->
  <dialog-base
    :visible.sync="visible"
    title="Danh sách phiếu thu chưa thanh toán"
    size="xl"
    :show-confirm="false"
    cancel-text="Đóng"
    cancel-type="danger"
    :use-panel="false"
    top="6vh"
    @cancel="handleClose"
  >
    <!-- Thông tin học sinh -->
    <div class="sk-panel mb-4">
      <div class="sk-panel-head">
        <div class="sk-panel-icon sk-panel-icon--warning">
          <i class="el-icon-user" />
        </div>
        <div>
          <p class="sk-panel-title">{{ studentName || 'Đang tải...' }}</p>
          <p class="sk-panel-subtitle">
            Mã học sinh: <strong>{{ studentCode || '—' }}</strong> &mdash; Lớp/Nhóm/Gói hiện tại
          </p>
        </div>
      </div>
    </div>

    <!-- Bảng danh sách phiếu thu chưa thanh toán -->
    <el-table
      v-loading="isLoading"
      :data="tableData"
      border
      style="width: 100%"
      class="sk-table"
      :empty-text="'Không có phiếu thu chưa thanh toán'"
    >
      <el-table-column type="index" label="STT" width="50" align="center" />
      
      <el-table-column label="Tháng-Năm" width="110" align="center">
        <template slot-scope="{ row }">
          {{ row.month }}/{{ row.year }}
        </template>
      </el-table-column>

      <el-table-column label="Đợt" prop="turn" width="70" align="center" />

      <el-table-column label="Mã phiếu thu" prop="invoiceCode" align="center" min-width="200">
        <template slot-scope="{ row }">
          <span>{{ row.invoiceCode || '—' }}</span>
        </template>
      </el-table-column>

      <el-table-column label="Lớp/nhóm" prop="targetName" align="center" min-width="140">
        <template slot-scope="{ row }">
          <span>{{ row.targetName || '—' }}</span>
        </template>
      </el-table-column>
      
      <el-table-column label="Thiếu/Thừa phiếu thu trước" align="right" min-width="130">
        <template slot-scope="{ row }">
          <span :class="row.beforeMoney < 0 ? 'tdi-money--negative' : 'tdi-money--positive'">
            {{ row.beforeMoney | formatCurrency }}
          </span>
        </template>
      </el-table-column>

      <el-table-column label="Phải thu" align="right" min-width="130">
        <template slot-scope="{ row }">
          <span class="tdi-money tdi-money--bold">{{ row.sumMoney | formatCurrency }}</span>
        </template>
      </el-table-column>

      <el-table-column label="Đã thu" align="right" min-width="130">
        <template slot-scope="{ row }">
          <span class="tdi-money">{{ row.collectedMoney | formatCurrency }}</span>
        </template>
      </el-table-column>

      <el-table-column label="Tiền mặt" align="right" min-width="130">
        <template slot-scope="{ row }">
          <span class="tdi-money">{{ row.cashMoney | formatCurrency }}</span>
        </template>
      </el-table-column>

      <el-table-column label="Chuyển khoản" align="right" min-width="130">
        <template slot-scope="{ row }">
          <span class="tdi-money">{{ row.transferMoney | formatCurrency }}</span>
        </template>
      </el-table-column>

      <el-table-column label="Thiếu/Thừa còn lại" align="right" min-width="130">
        <template slot-scope="{ row }">
          <span :class="row.afterMoney < 0 ? 'tdi-money--negative' : 'tdi-money--positive'">
            {{ row.afterMoney | formatCurrency }}
          </span>
        </template>
      </el-table-column>

      <el-table-column label="Tác vụ" width="80" align="center" fixed="right">
        <template slot-scope="{ row }">
          <el-tooltip content="Thanh toán" placement="top" :enterable="false">
            <el-button
              v-if="checkPermission(['tuition_payment_v4'])"
              size="mini"
              type="primary"
              icon="el-icon-wallet"
              circle
              @click="handlePayment(row)"
            />
          </el-tooltip>
        </template>
      </el-table-column>
    </el-table>
  </dialog-base>
</template>

<script>
import DialogBase from "@/components/DialogBase.vue";
import ManagingStudentInvoiceService from "@/services/tutition_v3/ManagingStudentInvoiceService";
import NotifyMessage from "@/commonFuncion/NotifyMessage";
import checkPermission from "@/utils/permission";

export default {
  name: "UnpaidInvoicesDialog",
  components: { DialogBase },
  data() {
    return {
      visible: false,
      isLoading: false,
      studentName: "",
      studentCode: "",
      tableData: [],
    };
  },
  methods: {
    checkPermission,
    // ─── Public API ────────────────────────────────────────────────
    /**
     * Mở dialog và load danh sách phiếu thu chưa thanh toán
     * @param {Object} row - dữ liệu dòng học sinh
     * @param {String} targetType - CLASS hoặc GROUP
     */
    open(row, targetType) {
      this.studentName = row.fullName || "";
      this.studentCode = row.studentCode || "";
      this.tableData = [];
      this.visible = true;
      this.fetchData(row.idStudent, targetType);
    },

    // ─── API ───────────────────────────────────────────────────────
    async fetchData(idStudent, targetType) {
      this.isLoading = true;
      try {
        const res = await ManagingStudentInvoiceService.getUnpaidInvoices(idStudent, targetType);
        const data = res?.data?.data || [];
        this.tableData = data.map((item) => ({
          ...item,
          beforeMoney: Number(item.beforeMoney || 0),
          sumMoney: Number(item.sumMoney || 0),
          collectedMoney: Number(item.collectedMoney || 0),
          cashMoney: Number(item.cashMoney || 0),
          transferMoney: Number(item.transferMoney || 0),
          afterMoney: Number(item.afterMoney || 0),
        }));
      } catch (err) {
        console.error(err);
        NotifyMessage.leftCornerNotificationError(
          (err && err.response && err.response.data && err.response.data.message) ||
            "Không thể tải danh sách phiếu thu chưa thanh toán!"
        );
      } finally {
        this.isLoading = false;
      }
    },

    handleClose() {
      this.visible = false;
    },

    handlePayment(row) {
      this.visible = false;
      this.$emit("payment", row);
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/style/sk-dialog.scss";

/* ─── Money helper classes ─────────────────────────────────── */
.tdi-money {
  font-variant-numeric: tabular-nums;
  color: #475569;
  font-size: 13px;
}
.tdi-money--bold     { font-weight: 700; color: #1e293b; }
.tdi-money--negative { color: #dc2626; font-weight: 600; }
.tdi-money--positive { color: #16a34a; font-weight: 600; }
</style>
